"use client";

// Desktop sidebar collapse state.
//
// Lives in context because two siblings need it: the shell reserves the rail's
// width, and NavList decides whether to render labels. Persisted so the choice
// survives navigation and reloads. Mobile is unaffected — the drawer renders
// NavList without `collapsible`.
//
// localStorage is an external store, so it is read through
// useSyncExternalStore: the server snapshot is "expanded", which keeps
// hydration consistent, and the stored value takes over on the client.

import { createContext, useCallback, useContext, useSyncExternalStore } from "react";

const STORAGE_KEY = "bell.sidebar.collapsed";

interface Ctx {
  collapsed: boolean;
  toggle: () => void;
}

const SidebarCtx = createContext<Ctx | null>(null);

const listeners = new Set<() => void>();

function getSnapshot(): boolean {
  return localStorage.getItem(STORAGE_KEY) === "1";
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Keep other tabs in step with this one.
  const onStorage = (e: StorageEvent) => e.key === STORAGE_KEY && listener();
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

function setCollapsed(next: boolean): void {
  localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
  for (const l of listeners) l();
}

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const collapsed = useSyncExternalStore(subscribe, getSnapshot, () => false);
  const toggle = useCallback(() => setCollapsed(!getSnapshot()), []);

  return <SidebarCtx.Provider value={{ collapsed, toggle }}>{children}</SidebarCtx.Provider>;
}

/** Falls back to "expanded" outside a provider: NavList also renders in the
 *  mobile drawer, which has no collapse behaviour to read. */
export function useSidebar(): Ctx {
  return useContext(SidebarCtx) ?? { collapsed: false, toggle: () => {} };
}
